import React, { memo, useMemo } from "react";

import { ShowcaseIntro } from "ui/components/Showcase";

import {
  USER_ACCOUNT_STEP,
  USER_CONTACT_STEP,
  USER_INFO_STEP,
  USER_INTRO_SAMPLE,
  USER_INTRO_TEXTS,
  USER_SUMMARY_STEP,
} from "./consts";
import UserAccountScene from "./scenes/UserAccountScene";
import UserContactScene from "./scenes/UserContactScene";
import UserInfoScene from "./scenes/UserInfoScene";
import UserSummaryScene from "./scenes/UserSummaryScene";
import { UserIntroData, UserIntroProps, UserSceneProps } from "./types";

/** Scena za korak čarobnjaka korisnika. */
const STEP_SCENES: Record<number, React.FC<UserSceneProps>> = {
  [USER_INFO_STEP]: UserInfoScene,
  [USER_ACCOUNT_STEP]: UserAccountScene,
  [USER_CONTACT_STEP]: UserContactScene,
  [USER_SUMMARY_STEP]: UserSummaryScene,
};

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");

/** Lijevi panel unosa korisnika: za svaki korak vlastita scena s upisanim podacima, a ispod objašnjenje. */
const UserIntro: React.FC<UserIntroProps> = ({ formValues, roleOptions, officeOptions, isEdit, stepCurrent, stepCount }) => {
  const stepKey = STEP_SCENES[stepCurrent] ? stepCurrent : USER_INFO_STEP;
  const Scene = STEP_SCENES[stepKey];
  const text = USER_INTRO_TEXTS[isEdit ? "edit" : "create"][stepKey];

  const user = useMemo<UserIntroData>(() => {
    const fullName = [formValues.firstName, formValues.lastName].filter(Boolean).join(" ") || USER_INTRO_SAMPLE.fullName;

    // Uloge u obrascu su ID-evi (ponekad objekti), a naziv se čita iz ponuđenih uloga.
    const roleNames = (formValues.roles ?? [])
      .map((role: any) => {
        const id = typeof role === "string" ? role : role?.id;
        return roleOptions.find((option) => option.value === id)?.text ?? role?.name ?? "";
      })
      .filter(Boolean);

    const officeName = officeOptions.find((option) => option.value === formValues.officeId)?.text;

    return {
      fullName,
      initials: getInitials(fullName),
      oib: formValues.oib ? String(formValues.oib) : USER_INTRO_SAMPLE.oib,
      userName: formValues.userName || USER_INTRO_SAMPLE.userName,
      email: formValues.email || USER_INTRO_SAMPLE.email,
      phone: formValues.phoneNumber || USER_INTRO_SAMPLE.phone,
      mobile: formValues.mobilePhoneNumber || USER_INTRO_SAMPLE.mobile,
      roleNames: roleNames.length > 0 ? roleNames : USER_INTRO_SAMPLE.roleNames,
      officeName: officeName ? String(officeName) : USER_INTRO_SAMPLE.officeName,
    };
  }, [formValues, roleOptions, officeOptions]);

  return (
    <ShowcaseIntro
      sceneKey={stepKey}
      title={text.title}
      description={text.description}
      stepCurrent={stepKey}
      stepCount={stepCount}
    >
      <Scene user={user} isEdit={isEdit} />
    </ShowcaseIntro>
  );
};

export default memo(UserIntro);
